/**
 * Sessionen som Providers.tsx läser vid uppstart och skickar till Keycloak-init.
 * Rensas vid utloggning och när användaren väljer "Logga in igen" på felsidan,
 * så att nästa uppstart går hela vägen via Keycloaks inloggning i stället
 * för att återanvända en token som inte fungerar.
 */
export const clearStoredSession = () => {
  sessionStorage.removeItem('kc_token');
  sessionStorage.removeItem('kc_refreshToken');
};

export interface StoredSession {
  token: string;
  refreshToken: string;
}

/** Sekunder som refresh-tokenet minst ska ha kvar för att vara värt att skicka till init */
const REFRESH_TOKEN_MIN_VALIDITY_S = 5;

/**
 * Läser exp ur en JWT utan att verifiera signaturen. Det räcker här:
 * Keycloak avgör ändå om tokenet gäller, vi vill bara slippa skicka in ett
 * som redan har gått ut. Ett token utan exp räknas som giltigt, precis som
 * i keycloak-js isTokenExpired. Ett oläsbart token ger null.
 */
const readTokenExp = (token: string): number | undefined | null => {
  try {
    const payload = token.split('.')[1];
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const exp: unknown = JSON.parse(json).exp;
    return typeof exp === 'number' ? exp : undefined;
  } catch {
    return null;
  }
};

/**
 * Tokens från sessionStorage, om refresh-tokenet fortfarande gäller.
 *
 * Det är refresh-tokenet som avgör: ett utgånget access-token är normalt
 * efter en stund och förnyas vid init. Ett utgånget refresh-token ger
 * däremot 400 från Keycloak, och då stod portalen tidigare kvar med
 * authenticated=true utan token. Är det utgånget eller oläsbart rensas
 * storage och init går via check-sso, som loggar in direkt om SSO-
 * sessionen lever och annars visar inloggningssidan.
 *
 * TermsAgreementModal tar bort kc_token men lämnar kc_refreshToken; utan
 * båda returneras inget, så även det fallet går via check-sso.
 */
export const readStoredSession = (): StoredSession | undefined => {
  const token = sessionStorage.getItem('kc_token');
  const refreshToken = sessionStorage.getItem('kc_refreshToken');

  if (!token || !refreshToken) {
    clearStoredSession();
    return undefined;
  }

  const exp = readTokenExp(refreshToken);
  const nowS = Math.ceil(Date.now() / 1000);

  if (exp === null || (exp !== undefined && exp - nowS < REFRESH_TOKEN_MIN_VALIDITY_S)) {
    clearStoredSession();
    return undefined;
  }

  return { token, refreshToken };
};
